import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../../api";
import { PageHeader } from "../GenericPage";
import { esc, printHtml } from "../../lib/format";
import { Notice, Stat } from "../../lib/ui";

/*
 * Daily stock reconciliation – port of stockrecon.php + save_reconciliation_ajax.php + print_reconciliation_report.php
 * (API: /api/stock/reconciliation..., backend/recon.py)
 * Enter the counted quantity per product and save the row: the difference is logged and the product's stock
 * becomes the counted figure. "Print difference report" prints today's saved entries with signature lines.
 */

const signed = (d) => (d > 0 ? `+${d}` : String(d));
const diffTone = (d) => (d > 0 ? "var(--up)" : d < 0 ? "var(--danger)" : "var(--muted)");

function reportHtml(r) {
  const rows = r.rows.length ? r.rows.map((x) => `<tr><td>${x.product_id}</td><td>${esc(x.product_name)}</td><td>${esc(x.manufacturer || "-")}</td>
    <td class="n">${x.original_quantity}</td><td class="n">${x.updated_quantity}</td><td class="n ${x.difference > 0 ? "p" : x.difference < 0 ? "m" : ""}">${signed(x.difference)}</td>
    <td>${esc(x.reconciled_by || "-")}</td><td>${esc((x.created_at || "").slice(11, 16))}</td></tr>`).join("")
    : `<tr><td colspan="8" style="text-align:center;color:#777">No reconciliation entries saved for this date.</td></tr>`;
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Stock Reconciliation Report - ${r.date}</title><style>
    body{font-family:Arial,sans-serif;font-size:13px;color:#111;margin:30px}h1{font-size:18px;margin-bottom:0}.sub{color:#555;margin:4px 0 20px}
    table{width:100%;border-collapse:collapse}th,td{border:1px solid #999;padding:6px 8px;text-align:left}th{background:#eee}
    td.n{text-align:right}.p{color:#16a34a;font-weight:bold}.m{color:#b91c1c;font-weight:bold}.sum{margin-top:18px}.sum table{width:auto}
    .sign{margin-top:50px;display:flex;justify-content:space-between}.sign div{width:45%;border-top:1px solid #333;padding-top:6px;text-align:center}</style></head><body>
    <h1>Stock Reconciliation - Difference Report</h1><div class="sub">Reconciliation date: <strong>${r.date}</strong> | Generated: ${new Date().toLocaleString("en-GB")}</div>
    <table><thead><tr><th>Product ID</th><th>Product name</th><th>Manufacturer</th><th>System qty</th><th>Counted qty</th><th>Difference</th><th>Reconciled by</th><th>Time</th></tr></thead><tbody>${rows}</tbody></table>
    <div class="sum"><table><tr><th>Items reconciled</th><td class="n">${r.items}</td></tr><tr><th>Items with a difference</th><td class="n">${r.items_with_diff}</td></tr>
    <tr><th>Total units short (system &gt; counted)</th><td class="n m">-${r.total_short}</td></tr><tr><th>Total units over (counted &gt; system)</th><td class="n p">+${r.total_over}</td></tr>
    <tr><th>Net variance</th><td class="n">${r.net}</td></tr></table></div>
    <div class="sign"><div>Reconciled by (signature)</div><div>Approved by (signature)</div></div></body></html>`;
}

export default function StockRecon() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [counts, setCounts] = useState({});
  const [remarks, setRemarks] = useState({});
  const [saving, setSaving] = useState({});
  const [rowErr, setRowErr] = useState({});
  const [q, setQ] = useState("");
  const [show, setShow] = useState("all");
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await api.get("/api/stock/reconciliation/today");
      setData(d);
      setCounts(Object.fromEntries(d.rows.map((r) => [r.product_id, String(r.saved ? r.updated_quantity : r.quantity)])));
      setRemarks(Object.fromEntries(d.rows.map((r) => [r.product_id, r.remarks || ""])));
    } catch (e) { setNotice({ tone: "err", text: e.message }); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const save = async (row) => {
    const v = counts[row.product_id];
    if (v === "" || Number(v) < 0) { setRowErr((x) => ({ ...x, [row.product_id]: "Enter a valid count" })); return; }
    setSaving((s) => ({ ...s, [row.product_id]: true })); setRowErr((x) => ({ ...x, [row.product_id]: null }));
    try {
      const r = await api.post("/api/stock/reconciliation", { product_id: row.product_id, updated_quantity: Number(v), remarks: remarks[row.product_id] });
      setData((d) => ({ ...d, rows: d.rows.map((x) => (x.product_id === row.product_id
        ? { ...x, saved: true, quantity: r.updated_qty, updated_quantity: r.updated_qty, original_quantity: r.original_qty, difference: r.difference, remarks: remarks[row.product_id] } : x)) }));
    } catch (e) { setRowErr((x) => ({ ...x, [row.product_id]: e.message })); }
    finally { setSaving((s) => ({ ...s, [row.product_id]: false })); }
  };

  const printReport = async () => {
    try { printHtml(reportHtml(await api.get("/api/stock/reconciliation", { date: data.date }))); }
    catch (e) { setNotice({ tone: "err", text: e.message }); }
  };

  const rows = useMemo(() => (data?.rows || []).filter((r) => {
    const t = q.trim().toLowerCase();
    if (t && !(r.product_name || "").toLowerCase().includes(t) && !(r.manufacturer || "").toLowerCase().includes(t) && String(r.product_id) !== t) return false;
    return show === "all" || (show === "saved" ? r.saved : !r.saved);
  }), [data, q, show]);
  const savedRows = (data?.rows || []).filter((r) => r.saved);

  const actions = (
    <>
      <Link to="/stockrecon_report" className="btn"><i className="fas fa-chart-column" /> Report</Link>
      <button type="button" className="btn primary" onClick={printReport} disabled={!data}><i className="fas fa-print" /> Print difference report</button>
    </>
  );

  return (
    <main className="pg" style={{ maxWidth: "80rem" }}>
      <PageHeader slug="stockrecon" actions={actions} />
      <Notice notice={notice} onClose={() => setNotice(null)} />
      {data && (
        <div className="ui-stats">
          <Stat icon="fa-calendar-day" tone="muted" label="Reconciliation date" value={data.date} />
          <Stat icon="fa-clipboard-check" tone="up" label="Saved today" value={`${savedRows.length} / ${data.rows.length}`} />
          <Stat icon="fa-arrow-down" tone="danger" label="Units short" value={savedRows.reduce((s, r) => s + Math.min(0, r.difference), 0)} />
          <Stat icon="fa-arrow-up" tone="up" label="Units over" value={`+${savedRows.reduce((s, r) => s + Math.max(0, r.difference), 0)}`} />
        </div>
      )}
      <section className="panel">
        <div className="toolbar">
          <div className="field grow"><div className="search"><i className="fas fa-magnifying-glass" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a product by name, manufacturer or ID" aria-label="Find product" /></div></div>
          <div className="seg-filter" role="group" aria-label="Show">
            {[["all", "All"], ["pending", "Pending"], ["saved", "Saved"]].map(([k, l]) => <button key={k} type="button" aria-pressed={show === k} onClick={() => setShow(k)}>{l}</button>)}
          </div>
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr><th>ID</th><th>Product</th><th className="num">System qty</th><th className="num">Counted qty</th><th>Remarks</th><th className="num">Difference</th><th>Status</th><th /></tr></thead>
            <tbody>
              {!data && <tr><td colSpan={8}><div className="skel" /></td></tr>}
              {rows.map((r) => (
                <tr key={r.product_id} style={r.saved ? { background: "#f3fbf6" } : undefined}>
                  <td className="mono">{r.product_id}</td>
                  <td><div style={{ fontWeight: 600, color: "var(--ink)" }}>{r.product_name}</div><div style={{ fontSize: ".75rem", color: "var(--muted)" }}>{r.manufacturer || "–"}</div></td>
                  <td className="num mono">{r.quantity}</td>
                  <td className="num"><input className="cell" type="number" min="0" value={counts[r.product_id] ?? ""} style={{ maxWidth: "6.5rem", marginLeft: "auto", textAlign: "right" }}
                    aria-label={`Counted quantity of ${r.product_name}`} onChange={(e) => setCounts((c) => ({ ...c, [r.product_id]: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(r); } }} onFocus={(e) => e.target.select()} /></td>
                  <td><input className="cell" value={remarks[r.product_id] ?? ""} placeholder="Optional" aria-label={`Remarks for ${r.product_name}`}
                    onChange={(e) => setRemarks((m) => ({ ...m, [r.product_id]: e.target.value }))} style={{ fontFamily: "inherit" }} /></td>
                  <td className="num mono" style={{ color: r.saved ? diffTone(r.difference) : "var(--muted)", fontWeight: 600 }}>{r.saved ? signed(r.difference) : "–"}</td>
                  <td><span className={`pill-tag ${r.saved ? "ok" : ""}`}>{r.saved ? "Saved" : "Pending"}</span>
                    {rowErr[r.product_id] && <div style={{ color: "var(--danger)", fontSize: ".75rem" }}>{rowErr[r.product_id]}</div>}</td>
                  <td><button type="button" className="btn sm primary" disabled={saving[r.product_id]} onClick={() => save(r)}>{saving[r.product_id] ? "Saving…" : "Save"}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          {data && rows.length === 0 && <div className="empty"><i className="fas fa-magnifying-glass" />No products match.</div>}
        </div>
      </section>
    </main>
  );
}
